import React, { JSX, useEffect } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import { ErrorStateView } from "@common/components/PageStateView/components/ErrorStateView";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { BaseTable } from "@common/components/ShakeTable/BaseTable";
import { useTableInterjections } from "@common/components/ShakeTable/ui/interjectedRows/useTableInterjections";
import { Spinner } from "@common/components/Spinner/Spinner";
import { useCrashesAttributes } from "@common/hooks/filtering/modules/useCrashesAttributes";
import { Attributes } from "@common/hooks/filtering/ui/Attributes";
import { AttributesContainer } from "@common/hooks/filtering/ui/Attributes.styles";
import { isCrossPlatform, isWebOs } from "@common/models";
import { isFreePlan } from "@common/util/workspaceUtil";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import useCrashReportsApiConsumer from "@main/pages/Crashes/consumers/useCrashReportsApiConsumer";
import { CrashesModuleAddAppFooter } from "@main/pages/shared/components/AppFooter/AddAppFooter/AddAppFooter";
import { CrashModuleFreePlanFooter } from "@main/pages/shared/components/AppFooter/FreePlanFooter/FreePlanFooter";
import { EmptyStateViewConf, EmptyTableView } from "@main/pages/shared/components/EmptyTableView/EmptyTableView";
import { CrashesModuleEmptyState } from "@main/pages/shared/components/ModuleEmptyState/CrashesModuleEmptyState";
import { SdkSetupSteps } from "@main/pages/shared/components/SDKSetup/SdkSetupSteps";
import { getCrashNotSupportedConfig, resolveEmptyStateView } from "@main/pages/shared/helpers/resolveEmptyStateView";

export default function CrashReportingListPageView(): JSX.Element {
  const { selectedApp, selectedWorkspace } = useAppSelectionContext();
  const { sendEvent } = useAnalytics();

  useEffect(() => {
    sendEvent("page_view", { page_title: "Crash reporting list" });
  }, [sendEvent]);

  const crashesAttributeState = useCrashesAttributes(false);

  const { items, loading, hasMore, total, error, crashTableProps, refetchData } = useCrashReportsApiConsumer({
    attributesProps: crashesAttributeState,
  });

  const hasFilter = Boolean(crashesAttributeState.validAttributes.length);

  const interjections = useTableInterjections("crashes", { hasActiveFilters: hasFilter });
  const isError = Boolean(error);

  useEffect(() => {
    if (crashesAttributeState.validAttributes) {
      crashTableProps.table.resetRowSelection();
    }
    // eslint-disable-next-line
  }, [crashesAttributeState.validAttributes]);

  // Show crashes not supported screen
  if (selectedApp && (isCrossPlatform(selectedApp) || (isWebOs(selectedApp) && !selectedApp.is_approved))) {
    const config = getCrashNotSupportedConfig(selectedApp) as EmptyStateViewConf;
    return (
      <>
        <PageWrap style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <EmptyTableView {...config} />
        </PageWrap>
      </>
    );
  }

  if (!selectedApp?.is_approved) {
    return <SdkSetupSteps />;
  }

  if (!loading && !isError && !total && !hasFilter) return <CrashesModuleEmptyState />;

  if (error && !hasFilter) {
    return (
      <ErrorStateView
        title="There was an error loading tickets. Try refreshing."
        subtitle="The link you followed may be broken, or the workspace may have been removed."
      />
    );
  }

  const hasItems = (items && items.length > 0) || total > 0;

  const emptyViewConfig = resolveEmptyStateView(
    loading,
    "crashes",
    hasItems,
    selectedApp,
    hasFilter,
    isError,
    refetchData,
  ) as EmptyStateViewConf | undefined;

  const buildAppFooter = () => {
    if (Boolean(selectedApp?.is_sample)) return <CrashesModuleAddAppFooter />;
    if (isFreePlan(selectedWorkspace) && !Boolean(hasMore)) return <CrashModuleFreePlanFooter />;
    return null;
  };

  return (
    <>
      <PageWrap style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <AttributesContainer>
          <Attributes
            {...crashesAttributeState}
            searchField="title"
            enableKeyboardShortcut={true}
          />
        </AttributesContainer>
        {emptyViewConfig && (
          <EmptyTableView
            withFilters
            {...emptyViewConfig}
          />
        )}
        {loading && <Spinner />}
        {!emptyViewConfig && !loading && (
          <BaseTable
            {...crashTableProps}
            interjections={interjections}
            footer={buildAppFooter()}
            isFetchingNext={crashTableProps.isFetchingNext}
          />
        )}
      </PageWrap>
    </>
  );
}
